import { useEffect, useState } from "react";
import { format } from "date-fns";
import { supabase } from "../../lib/supabase";
import type { Deal } from "../../lib/types";

import DealLink from "../DealLink";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";
function formatAddress(d: Deal) {
  return [
    d.property_street,
    [d.property_city, d.property_state].filter(Boolean).join(", "),
  ]
    .filter(Boolean)
    .join(" · ");
}

export default function UnableToFlyTab() {
  const [deals, setDeals] = useState<Deal[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError(null);
      const { data, error } = await supabase
        .from("deals")
        .select(
          "id,homeowner_name,property_street,property_city,property_state,claim_number,stage,phase,drone_unable_to_fly,drone_unable_reason,needs_climb,drone_scheduled_date,assigned_drone_pilot_id",
        )
        .eq("drone_unable_to_fly", true)
        .order("drone_scheduled_date", { ascending: false });
      if (error) {
        if (!cancelled) setError(error.message);
        return;
      }
      if (!cancelled) setDeals((data ?? []) as Deal[]);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error)
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded">
        Couldn't load unable-to-fly list — {error}
      </div>
    );
  if (!deals)
    return <div className="text-slate-500 p-4">Loading…</div>;

  return (
    <div>
      <div className="mb-3 text-sm text-slate-600">
        {deals.length} {deals.length === 1 ? "deal" : "deals"} flagged for
        physical climb inspection. Joy authorizes → Robert/RS performs.
      </div>

      {deals.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-slate-500">
          Nothing flagged unable-to-fly.
        </div>
      ) : (
        <ul className="space-y-2">
          {deals.map((d) => (
            <li key={d.id} className="bg-white rounded-lg shadow-sm px-4 py-3">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                      Needs climb
                    </span>
                    {d.stage === "needs_climb_inspection" && (
                      <span className="text-xs text-slate-500">
                        Waiting on Joy authorization
                      </span>
                    )}
                    {d.drone_scheduled_date && (
                      <span className="text-xs text-slate-400">
                        Flagged {format(new Date(d.drone_scheduled_date), "MMM d")}
                      </span>
                    )}
                    {d.claim_number && (
                      <span className="text-xs text-slate-400">
                        #{d.claim_number}
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-bardin-ink">
                    <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                  </div>
                  <div className="text-sm text-slate-600">
                    {isAddressLinkable(d) ? (
                      <a
                        href={mapsHref(d)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="hover:underline hover:text-slate-800"
                      >
                        {formatAddress(d)}
                      </a>
                    ) : (
                      formatAddress(d) || "(no address)"
                    )}
                  </div>
                  {d.drone_unable_reason && (
                    <div className="text-sm text-slate-500 mt-1 italic">
                      "{d.drone_unable_reason}"
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
